<template>
  <delete-data-request-modal
    :status-delete-data-request-modal="statusDeclineRequestModal"
    :selected-request="selectedRequest"
    isPendingRequest
    title="Reprovar solicitação"
    sub-title="Para reprovar uma solicitação, você deve apontar um motivo para essa decisão:"
    @closeModal="closeDeclineRequestModal"
  >
    <v-card-text class="pb-0">
      <v-row>
        <v-col cols="12">
          <mf-loading loader-type="spinner" :boolean-loader="repliesLoading">
            <section>
              <v-radio-group :value="selectedReply" class="my-0 py-0">
                <v-expansion-panels v-model="indexReplyTextExpanded" flat>
                  <v-expansion-panel v-for="reply in replies" :key="reply._id" class="my-0">
                    <v-expansion-panel-header class="pa-0" @click="toggleExpansionPanel(reply)">
                      <v-radio :label="`Recusar por ${reply.title}`" :value="reply"></v-radio>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-textarea
                        v-model="reply.text"
                        solo
                        flat
                        rows="11"
                        background-color="grey lighten-5"
                        color="grey darken-1"
                        class="reply-textarea ml-2"
                        spellcheck="false"
                        no-resize
                        :error-messages="errorSelectedReply"
                      ></v-textarea>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-radio-group>
            </section>
          </mf-loading>
        </v-col>
        <v-col cols="12">
          <v-row>
            <v-layout class="col-4 align-center">
              <mf-toggle v-model="triggerEmail" label="Disparar por e-mail" hide-details disabled></mf-toggle>
            </v-layout>

            <v-layout class="col-8 justify-end align-center">
              <mf-button label="Cancelar" class="mr-2" text @click="closeDeclineRequestModal" />
              <mf-button label="Confirmar reprovação" :disabled="!selectedReply" @click="confirmDeclineRequest" />
            </v-layout>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </delete-data-request-modal>
</template>

<script>
import { maxLength } from 'vuelidate/lib/validators'
import { mustContainMinLengthCharsNoWhiteSpace } from '@/helpers/validators.js'
import { QUERY_STANDARD_USER_REPLIES, MUTATION_DECLINE_USER_DELETE_DATA_REQUEST } from '@/modules/clientes/gerenciamento/graphql'
import DeleteDataRequestModal from '@/modules/clientes/gerenciamento/views/components/DeleteDataRequestModal.vue'

export default {
  name: 'DeclineRequestModal',

  components: {
    DeleteDataRequestModal,
  },

  props: {
    statusDeclineRequestModal: {
      type: Boolean,
      required: true,
    },

    selectedRequest: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      triggerEmail: true,
      selectedReply: null,
      replies: [],
      repliesLoading: false,
      indexReplyTextExpanded: null,
      validationMinLength: 15,
      validationMaxLength: 1150,
    }
  },

  computed: {
    errorSelectedReply() {
      const currentReply = this.$v.selectedReply.text

      if (currentReply.$error) {
        if (!currentReply.mustContainMinLengthCharsNoWhiteSpace) {
          return `O motivo deve possuir pelo menos ${this.validationMinLength} caracteres sem espaços em branco`
        } else if (!currentReply.maxLength) return `O motivo excedeu ${this.validationMaxLength} caracteres`
      }

      return ''
    },
  },

  watch: {
    selectedRequest(currentRequest) {
      if (currentRequest?._id) this.getStandardUserReplies()
    },
  },

  methods: {
    async confirmDeclineRequest() {
      this.$v.$touch()

      if (!this.selectedReply.text || this.$v.$error) {
        this.$snackbar({ message: 'Preencha o campo motivo corretamente', snackbarColor: 'error' })
      } else {
        try {
          const {
            data: { declineUserDeleteDataRequest },
          } = await this.$apollo.mutate({
            mutation: MUTATION_DECLINE_USER_DELETE_DATA_REQUEST,
            variables: {
              user_id: this.selectedRequest?._id,
              trigger_email: this.triggerEmail,
              reply: this.selectedReply.text,
            },
          })
          const { requestWasModified, records } = declineUserDeleteDataRequest

          if (requestWasModified) {
            this.$emit('updateTable')
            this.$emit('setTotalDeleteDataRequest', records)
            this.$snackbar({ message: 'Solicitação reprovada com sucesso', snackbarColor: 'success' })
          } else {
            this.$snackbar({ message: 'Nenhuma solicitação foi reprovada', snackbarColor: 'error' })
          }

          this.closeDeclineRequestModal()
        } catch (error) {
          console.error(error)
          this.$snackbar({ message: 'Erro ao tentar enviar dados para o servidor', snackbarColor: 'error' })
        }
      }
    },

    async getStandardUserReplies() {
      this.repliesLoading = true

      try {
        const {
          data: { standardUserReplies },
        } = await this.$apollo.query({
          query: QUERY_STANDARD_USER_REPLIES,
          variables: {
            user_id: this.selectedRequest?._id,
            type: 'DELETE_DATA_REQUEST',
          },
          fetchPolicy: 'no-cache',
        })
        this.replies = standardUserReplies || []
        this.repliesLoading = false
      } catch (error) {
        console.error(error)
        this.$snackbar({ message: 'Erro ao tentar buscar dados no servidor', snackbarColor: 'error' })
        this.repliesLoading = false
      }
    },

    clearLocalStateModal() {
      this.triggerEmail = true
      this.selectedReply = null
      this.indexReplyTextExpanded = null
    },

    closeDeclineRequestModal() {
      this.clearLocalStateModal()
      this.$emit('closeDeclineRequestModal')
    },

    toggleExpansionPanel(reply) {
      if (this.indexReplyTextExpanded === undefined || this.indexReplyTextExpanded === null || this.selectedReply?._id !== reply?._id) {
        this.selectedReply = reply
      } else {
        this.selectedReply = null
      }
    },
  },

  validations() {
    return {
      selectedReply: {
        text: {
          maxLength: maxLength(this.validationMaxLength),
          mustContainMinLengthCharsNoWhiteSpace: (value) => mustContainMinLengthCharsNoWhiteSpace(value, this.validationMinLength),
        },
      },
    }
  },
}
</script>

<style scoped>
.reply-textarea::v-deep textarea {
  color: #757575 !important;
  font-size: 0.875rem;
}

.reply-textarea::v-deep .v-input__slot {
  padding: 7px 20px !important;
}
</style>
